import CreateBadgeModalButton from "@/admin/experiences/badges/CreateBadgeModalButton"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { useLabel } from "@/core/context/LabelsContext"
import FormStore from "@/core/form/store/FormStore"
import ExperienceSettingsAdditionalDetails from "@/product/settings/ExperienceSettingsAdditionalDetails"
import ExperienceSettingsDurationFields from "@/product/settings/ExperienceSettingsDurationFields"
import { ExperienceSettingsFormState } from "@/product/settings/ExperienceSettingsForm"
import ExperienceSettingsLabel from "@/product/settings/ExperienceSettingsLabel"
import ExperienceSettingsMetaDetailsFormFields from "@/product/settings/ExperienceSettingsMetaDetailsFormFields"
import ProductSettingsTags from "@/product/settings/ProductSettingsTags"
import ProductSlugInput from "@/product/settings/ProductSlugInput"
import { ExperienceSettingsCollapsibleDetailsFormFieldsFragment$key } from "@/product/settings/__generated__/ExperienceSettingsCollapsibleDetailsFormFieldsFragment.graphql"
import { ExperienceSettingsFormMutation } from "@/product/settings/__generated__/ExperienceSettingsFormMutation.graphql"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import ScopedFeature from "@components/scoped-feature/ScopedFeature"
import { DiscoDivider, DiscoFormControl, DiscoInput, DiscoText } from "@disco-ui"
import DiscoColorPicker from "@disco-ui/color/DiscoColorPicker"
import DiscoImageFormField from "@disco-ui/file/DiscoImageFormField"
import DiscoFormCollapsibleSection, {
  DiscoFormCollapsibleSectionSkeleton,
} from "@disco-ui/form-collapsible-section/DiscoFormCollapsibleSection"
import { useMediaQuery, useTheme } from "@material-ui/core"
import { useScopedFeatures } from "@utils/hook/useScopedFeatures"
import { ASPECT_RATIOS } from "@utils/image/imageConstants"
import { observer } from "mobx-react-lite"
import { graphql, useFragment } from "react-relay"

interface Props {
  form: FormStore<ExperienceSettingsFormState, ExperienceSettingsFormMutation>
  productKey: ExperienceSettingsCollapsibleDetailsFormFieldsFragment$key
}

function ExperienceSettingsCollapsibleDetailsFormFields({ form, productKey }: Props) {
  const experienceLabel = useLabel("admin_experience")
  const membersLabel = useLabel("admin_member")
  const classes = useStyles()

  const isScopedFeatureEnabled = useScopedFeatures()

  const theme = useTheme()
  const isMdScreenUp = useMediaQuery(theme.breakpoints.up("md"))

  const product = useFragment<ExperienceSettingsCollapsibleDetailsFormFieldsFragment$key>(
    graphql`
      fragment ExperienceSettingsCollapsibleDetailsFormFieldsFragment on Product {
        id
        ...ExperienceSettingsDurationFieldsFragment
      }
    `,
    productKey
  )

  const experienceDrawer = useGlobalDrawer("experienceSettings")
  const sectionParam = experienceDrawer.params.experienceSettingsSection
  const isMetaDetailsOpen = sectionParam === "meta-details"

  return (
    <>
      <DiscoFormCollapsibleSection
        sectionId={"general-details"}
        icon={"edit"}
        title={"General Details"}
        description={`Customize the details of this ${experienceLabel.singular} such as name, description, cover image.`}
        openByDefault={!isMetaDetailsOpen}
      >
        <DiscoFormCollapsibleSection.Group>
          <DiscoText marginBottom={2.5} variant={"body-md-600"}>
            {`${experienceLabel.singular} Information`}
          </DiscoText>
          <DiscoFormControl
            sublabel={`Select a badge and a short name for this ${experienceLabel.singular}.`}
            error={Boolean(form.errorsByField.name || form.errorsByField.badgeId)}
            errorMessages={form.errorsByField.name || form.errorsByField.badgeId}
          >
            <DiscoInput
              value={form.state.name}
              onChange={(e) => (form.state.name = e.target.value)}
              fullWidth
              data-testid={"ExperienceSettingsDetailsFormFields.name"}
              startAdornment={
                <CreateBadgeModalButton
                  badge={form.state.badge!}
                  testid={"ExperienceSettingsDetailsFormFields.badge-picker"}
                />
              }
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </DiscoFormControl>
          <DiscoFormControl
            sublabel={`A short description to show as a preview when you share this ${experienceLabel.singular}.`}
            error={Boolean(form.errorsByField.description)}
            errorMessages={form.errorsByField.description}
          >
            <DiscoInput
              value={form.state.description}
              onChange={(e) => (form.state.description = e.target.value)}
              fullWidth
              multiline
              data-testid={"ExperienceSettingsDetailsFormFields.description"}
            />
          </DiscoFormControl>
          <DiscoFormControl
            variant={isMdScreenUp ? "two-column" : "one-column"}
            sublabel={`Add a cover image for use on ${experienceLabel.singular} cards, registration, and more!`}
            error={Boolean(form.errorsByField.cover)}
            errorMessages={form.errorsByField.cover}
          >
            <DiscoImageFormField
              value={form.state.cover}
              classes={{ image: classes.imageFormField }}
              onChange={(result) => {
                form.state.cover = result?.url || null
                form.state.coverAssetId = result?.id
              }}
              disableRemove
              suggestedDimensions={{ width: 1200, height: 600 }}
              cropperProps={{
                stencilProps: { aspectRatio: ASPECT_RATIOS.COVER_PHOTO },
              }}
              alterAspectRatio={false}
              maxWidth={"fit-content"}
              showIcons
            />
          </DiscoFormControl>
        </DiscoFormCollapsibleSection.Group>
        <DiscoFormCollapsibleSection.Group>
          <ExperienceSettingsDurationFields
            form={form}
            mode={"edit"}
            productKey={product}
          />
        </DiscoFormCollapsibleSection.Group>
      </DiscoFormCollapsibleSection>
      <DiscoDivider marginTop={2.5} marginBottom={2.5} />
      <DiscoFormCollapsibleSection
        sectionId={"advanced"}
        icon={"widget-alt"}
        title={"Advanced"}
        description={
          isScopedFeatureEnabled("product.type-tag")
            ? "Customize label, tags, the URL and details on the registration page."
            : "Customize tags, the URL and details on the registration page."
        }
        openByDefault={isMetaDetailsOpen}
      >
        <ScopedFeature
          feature={"product.type-tag"}
          enabled={
            <DiscoFormCollapsibleSection.Group>
              <DiscoFormControl
                label={
                  <ExperienceSettingsLabel
                    title={"Label"}
                    sectionId={"experience_label"}
                    tooltipContent={`A label is used across the platform to indicate the type of experience. For example, 'Course' or 'Space'.`}
                  />
                }
                sublabel={`Label the ${experienceLabel.singular} to indicate its type.`}
                testid={"ExperienceSettingsDetailsFormFields.ProductTypeField"}
                error={Boolean(form.errorsByField.typeTag)}
                errorMessages={form.errorsByField.typeTag}
              >
                <DiscoInput
                  value={form.state.typeTag!.label}
                  onChange={(result) => (form.state.typeTag!.label = result.target.value)}
                  inputProps={{ maxLength: 20 }}
                  endAdornment={
                    <DiscoColorPicker
                      endAdornment={true}
                      className={classes.tagLabelColorPicker}
                      hideText={true}
                      formControlProps={{ marginBottom: 0 }}
                      value={form.state.typeTag?.color}
                      onChange={(v) => (form.state.typeTag!.color = v.toUpperCase())}
                    />
                  }
                />
              </DiscoFormControl>
            </DiscoFormCollapsibleSection.Group>
          }
        />
        <DiscoFormCollapsibleSection.Group>
          <ProductSettingsTags form={form} kind={"experience"} />
        </DiscoFormCollapsibleSection.Group>
        <DiscoFormCollapsibleSection.Group>
          <DiscoFormControl
            label={
              <ExperienceSettingsLabel
                title={"URL"}
                sectionId={"experience_page"}
                tooltipContent={`The base URL where your ${experienceLabel.singular} lives. Visible to different ${membersLabel.plural} depending on your setup in the "Access" tab.`}
              />
            }
            sublabel={`The base URL for your ${experienceLabel.singular}.`}
            testid={`ExperienceSettingsDetailsFormFields.ProductSlugField`}
            error={Boolean(form.errorsByField.experienceUrl)}
            errorMessages={form.errorsByField.experienceUrl}
          >
            <ProductSlugInput
              testid={"ExperienceSettingsDetailsFormFields"}
              value={form.state.experienceUrl}
              onChange={(e) => (form.state.experienceUrl = e.target.value)}
            />
          </DiscoFormControl>
        </DiscoFormCollapsibleSection.Group>
        <DiscoFormCollapsibleSection.Group>
          <ExperienceSettingsMetaDetailsFormFields form={form} />
        </DiscoFormCollapsibleSection.Group>
        <DiscoFormCollapsibleSection.Group>
          <ExperienceSettingsAdditionalDetails productId={form.state.id} />
        </DiscoFormCollapsibleSection.Group>
      </DiscoFormCollapsibleSection>
    </>
  )
}

const useStyles = makeUseStyles((theme) => ({
  tagLabelColorPicker: {
    "&.Mui-focused": {
      border: "none",
    },
    "& button": {
      border: "2px solid #D5D9DE",
    },
    height: "40px",
  },
  skeletonContainer: {
    display: "flex",
    flexDirection: "column",
  },
  imageFormField: {
    [theme.breakpoints.up("md")]: {
      width: "300px",
    },
  },
}))

export const ExperienceSettingsCollapsibleDetailsFormFieldsSkeleton = () => {
  const classes = useStyles()

  return (
    <div className={classes.skeletonContainer}>
      <DiscoFormCollapsibleSectionSkeleton />
      <DiscoDivider marginBottom={2.5} marginTop={2.5} />
      <DiscoFormCollapsibleSectionSkeleton />
      <DiscoDivider marginBottom={2.5} marginTop={2.5} />
      <DiscoFormCollapsibleSectionSkeleton />
    </div>
  )
}

export default Relay.withSkeleton<Props>({
  component: observer(ExperienceSettingsCollapsibleDetailsFormFields),
  skeleton: ExperienceSettingsCollapsibleDetailsFormFieldsSkeleton,
})
